import isEqual from 'lodash/isEqual'
import { createSelector } from 'reselect'
import type { State } from 'types/redux'
import type { Comment, FullWriterSplit, NativeTag, Track } from 'types/track'
import {
  convertSplitsToFullForm,
  getContactsList,
  getPros,
  getPublishers,
} from '@disco/state-management/selectors/contacts'
import {
  getTrack,
  getTracks,
  getTracksByIds,
} from '@disco/state-management/selectors/tracks'
import { pick } from '@disco/utils/array'
const EMPTY_ARRAY = []
/**
 * Gets the copy metadata state.
 * @param state - The Redux state
 * @returns The copy metadata state object
 */
export const selectCopyMetadataState = (state: State): State['copyMetadata'] =>
  state.copyMetadata
/**
 * Gets the source track for metadata copying.
 * @param state - The Redux state
 * @returns The source track object, or null if no source track is selected
 */
export const selectCopyMetadataSourceTrack = (
  state: State,
): Track | null | undefined => {
  const { sourceTrackId } = selectCopyMetadataState(state)
  return sourceTrackId
    ? getTrack(state, {
        id: sourceTrackId,
      })
    : null
}
/**
 * Gets the target tracks for metadata copying.
 * @param state - The Redux state
 * @returns Array of target track objects
 */
export const selectCopyMetadataTargetTracks = (state: State): Track[] => {
  const { targetTrackIds } = selectCopyMetadataState(state)
  return targetTrackIds
    ? getTracksByIds(state, {
        ids: targetTrackIds,
      })
    : []
}
/**
 * Gets the full writer splits for the source track with contact information populated.
 * @param state - The Redux state
 * @returns Array of writer splits with full contact details
 */
const selectCopyMetadataSourceFullWriterSplits = createSelector(
  [selectCopyMetadataSourceTrack, getContactsList, getPros, getPublishers],
  (track, contacts, pros, publishers) => {
    return track?.track_writers
      ? convertSplitsToFullForm(track.track_writers, contacts, pros, publishers)
      : EMPTY_ARRAY
  },
)
/**
 * Gets the selected source fields for metadata copying based on field selection.
 * @param state - The Redux state
 * @param fields - Object containing field selection configuration
 * @returns Object with selected fields from the source track
 */
export const selectCopyMetadataSourceFields = createSelector(
  [
    selectCopyMetadataSourceTrack,
    selectCopyMetadataSourceFullWriterSplits,
    (_state, fields) => fields,
  ],
  (track, writers, fields) => {
    const {
      artwork,
      comments_indexes,
      metadata_keys,
      native_tags_indexes,
      track_writers_indexes,
    } = fields
    if (!track) return {}
    const comments: Comment[] = []

    for (const key in comments_indexes) {
      const comment = comments_indexes[key] && track.comments?.[Number(key)]
      if (comment) comments.push(comment)
    }

    const metadata = {}

    for (const key in metadata_keys) {
      if (metadata_keys[key]) {
        metadata[key] = track.metadata[key] ?? null
      }
    }

    const native_tags: NativeTag[] = []

    for (const key in native_tags_indexes) {
      const tag = native_tags_indexes[key] && track.native_tags?.[Number(key)]
      if (tag) native_tags.push(tag)
    }

    const track_writers: FullWriterSplit[] = []

    for (const key in track_writers_indexes) {
      const writer = track_writers_indexes[key] && writers?.[Number(key)]
      if (writer) track_writers.push(writer)
    }

    return {
      artwork: artwork ? track.artwork : undefined,
      comments,
      metadata,
      native_tags,
      track_writers,
    }
  },
)
/**
 * Calculates the number of changes that would be made when copying metadata to target tracks.
 * @param state - The Redux state
 * @param fields - Object containing field selection configuration
 * @returns Object mapping track IDs to the number of changes that would be made
 */
export const selectCopyMetadataChanges = createSelector(
  [selectCopyMetadataState, getTracks, (_state, fields) => fields],
  (state, tracks, fields) => {
    const { sourceTrackId, targetTrackIds } = state

    if (
      !fields.metadata_keys ||
      !sourceTrackId ||
      targetTrackIds.length === 0
    ) {
      return {}
    }

    const sourceTrack: Track = tracks[sourceTrackId]
    return targetTrackIds.reduce((accumulator, trackId) => {
      const result = accumulator
      let count = 0
      const metadataChanges = Object.keys(fields.metadata_keys)
        .map((key: string) => {
          if (
            fields.metadata_keys[key] &&
            tracks[trackId].metadata[key] !== sourceTrack.metadata[key]
          ) {
            return true
          }

          return false
        })
        .filter(Boolean)
      count = metadataChanges.length

      if (fields.artwork) {
        count = count + 1
      }

      const trackWritersChanges = Object.keys(fields.track_writers_indexes)
        .map((key: string) => fields.track_writers_indexes[key])
        .filter(Boolean)

      if (
        trackWritersChanges.length > 0 &&
        !isEqual(
          sourceTrack.track_writers?.map(writerSplit =>
            pick(writerSplit, ['pro', 'publisher', 'split', 'writer']),
          ),
          tracks[trackId].track_writers?.map(writerSplit =>
            pick(writerSplit, ['pro', 'publisher', 'split', 'writer']),
          ),
        )
      ) {
        count = count + 1
      }

      if (count > 0) {
        result[trackId] = count
      }

      return result
    }, {})
  },
)
